import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Box,
  Center,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
  useDisclosure,
  useTheme,
  VStack,
} from '@chakra-ui/react';
import {AnimatePresence, motion} from 'framer-motion';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';
import ArticleModal from '../../components/Articles/Modal';
import PlaceCard from '../../components/Articles/PlaceCard';
import BookmarkButton from '../../components/BookmarkButton';
import BookmarkAheadLocationPicker, {
  DEFAULT_HOME_SELECTION,
  SelectedLocation,
} from '../../components/BookmarkAheadLocationPicker';
import {BlueButton, CustomButton} from '../../components/button';
import Icon from '../../components/Icon';
import {TextG12, TextG14, TextG16, TextG24} from '../../components/typography';
import {Place} from '../../types/article';
import {useAddBookmark, useRemoveBookmark} from '../../hooks/useBookmark';
import {ToastMessage} from '../../components/toast';
import {useBookmarkPlacesFeedState} from './useBookmarkPlacesFeedState';

interface BookmarkPlacesProps {
  onClose?: () => void;
  // When true: no fixed positioning, no close button, no exit nav — used by
  // the onboarding wrapper so the screen lives inside the onboarding shell.
  embedded?: boolean;
  onSavedCountChange?: (count: number) => void;
  showExitConfirmation?: boolean;
  onExitConfirmationClose?: () => void;
  onExitConfirmationSkip?: () => void;
}

const BookmarkPlaces = ({
  onClose,
  embedded = false,
  onSavedCountChange,
  showExitConfirmation: externalExitConfirmation,
  onExitConfirmationClose,
  onExitConfirmationSkip,
}: BookmarkPlacesProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {isOpen, onOpen, onClose: closeArticleModal} = useDisclosure();
  const hasLoggedSession = useRef(false);

  const [selectedLocation, setSelectedLocation] = useState<SelectedLocation>(DEFAULT_HOME_SELECTION);
  const overrideParams =
    selectedLocation.latitude != null && selectedLocation.longitude != null
      ? {latitude: selectedLocation.latitude, longitude: selectedLocation.longitude}
      : undefined;

  const {
    nearestYou,
    popularNeighbors,
    popularSanDiego,
    loading,
    bannerRef,
    bannerHeight,
  } = useBookmarkPlacesFeedState(overrideParams);

  // Session-only: the feed excludes already-bookmarked places server-side, so
  // every card starts unsaved and `savedIds` counts only this session's adds.
  // `savedPlaces` keeps the full Place objects (most-recent first) so the Saved
  // section can keep rendering them after the feed refetch drops them.
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [savedPlaces, setSavedPlaces] = useState<Place[]>([]);
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  const [internalExitConfirmation, setInternalExitConfirmation] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const addBookmarkMutation = useAddBookmark();
  const removeBookmarkMutation = useRemoveBookmark();
  const queryClient = useQueryClient();

  // Fly-to-save: each entry animates a small bookmark disc from the tapped (+) button
  // to the "X Saved" pill in the header. Mirrors the events feed (BookmarkAhead).
  const savedPillRef = useRef<HTMLButtonElement>(null);
  const savedPlacesRef = useRef<HTMLDivElement>(null);
  const flyKeyRef = useRef(0);
  const [flyingItems, setFlyingItems] = useState<
    Array<{key: number; from: {x: number; y: number}}>
  >([]);

  const triggerFlyToSave = useCallback((origin: {x: number; y: number}) => {
    // Defer one frame: on the user's first save, the Saved pill is mounting in
    // the same render. The fly overlay reads `savedPillRef.current` at render
    // time, so without a deferral it's null on save #1 and the animation skips.
    requestAnimationFrame(() => {
      flyKeyRef.current += 1;
      const key = flyKeyRef.current;
      setFlyingItems((prev) => [...prev, {key, from: origin}]);
    });
  }, []);

  const removeFlyingItem = useCallback((key: number) => {
    setFlyingItems((prev) => prev.filter((item) => item.key !== key));
  }, []);

  const handleToggleBookmark = useCallback(
    (place: Place) => {
      const id = place.id.toString();
      if (pendingIds.has(id)) return;
      const willSave = !savedIds.has(id);

      setSavedIds((prev) => {
        const next = new Set(prev);
        if (willSave) next.add(id);
        else next.delete(id);
        return next;
      });
      setSavedPlaces((prev) => {
        const without = prev.filter((p) => p.id.toString() !== id);
        return willSave ? [place, ...without] : without;
      });
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });

      const mutation = willSave ? addBookmarkMutation : removeBookmarkMutation;
      mutation.mutate(
        {articleId: place.id, type: 'place'},
        {
          // Intentionally no onSuccess refetch: the optimistic savedIds + filterSaved
          // hide the just-saved card client-side. Refetching here causes the feed to
          // re-render mid-scroll (visible glitch where the page jumps a beat later).
          onError: () => {
            setSavedIds((prev) => {
              const next = new Set(prev);
              if (willSave) next.delete(id);
              else next.add(id);
              return next;
            });
            setSavedPlaces((prev) => {
              const without = prev.filter((p) => p.id.toString() !== id);
              return willSave ? without : [place, ...without];
            });
            ToastMessage({
              status: 'error',
              text: willSave
                ? 'Failed to bookmark. Please try again.'
                : 'Failed to remove bookmark. Please try again.',
            });
          },
          onSettled: () => {
            setPendingIds((prev) => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
          },
        },
      );
    },
    [savedIds, pendingIds, addBookmarkMutation, removeBookmarkMutation, queryClient],
  );

  const bookmarkedCount = savedIds.size;
  const needsExitConfirmation = bookmarkedCount < 3;

  // Onboarding wraps this screen and controls its own exit modal; standalone use
  // owns the modal locally. `showExitConfirmation` reflects whichever owner is active.
  const showExitConfirmation = embedded
    ? !!externalExitConfirmation
    : internalExitConfirmation;
  const setShowExitConfirmation = embedded
    ? (open: boolean) => {
        if (!open) onExitConfirmationClose?.();
      }
    : setInternalExitConfirmation;

  useEffect(() => {
    onSavedCountChange?.(savedIds.size);
  }, [savedIds.size, onSavedCountChange]);

  // The feed query key is stable (does not include lat/long), so location changes
  // need an explicit invalidate. Mirrors BookmarkAhead's behavior for the events feed.
  const isFirstLocationRender = useRef(true);
  useEffect(() => {
    if (isFirstLocationRender.current) {
      isFirstLocationRender.current = false;
      return;
    }
    queryClient.invalidateQueries(['bookmark-places-feed']);
  }, [selectedLocation.latitude, selectedLocation.longitude, queryClient]);

  // TODO(CT-6370 backend): wire useLogTouchpoint('bookmark_places_session') once
  // the backend adds the slug to TouchpointSlug.
  useEffect(() => {
    if (!hasLoggedSession.current && !loading) {
      hasLoggedSession.current = true;
    }
  }, [loading]);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }
    if (needsExitConfirmation && bookmarkedCount > 0) {
      setShowExitConfirmation(true);
      return;
    }
    navigate('/home');
  }, [onClose, needsExitConfirmation, bookmarkedCount, navigate]);

  useEffect(() => {
    if (searchParams.has('id')) onOpen();
  }, [searchParams, onOpen]);

  const loadType = searchParams.get('articleType')?.toLowerCase() ?? 'place';

  const sections: Array<{
    key: string;
    title: string;
    subtitle: string;
    items: Place[];
    boxRef?: React.RefObject<HTMLDivElement>;
  }> = useMemo(
    () => {
      // Optimistic removal: drop session-saved places from discovery sections so
      // they vanish on tap, before the feed refetch lands. The server already excludes
      // pre-existing bookmarks, so this only filters new ones.
      const filterSaved = (items: Place[]) =>
        items.filter((p) => !savedIds.has(p.id.toString()));
      return [
        {
          key: 'nearest',
          title: 'Nearest You',
          subtitle: 'Save your faves and ones you might try.',
          items: filterSaved(nearestYou),
        },
        {
          key: 'pop-nbrs',
          title: 'Popular With Your Neighbors',
          subtitle: 'Save your faves and ones you might try.',
          items: filterSaved(popularNeighbors),
        },
        {
          key: 'pop-sd',
          title: 'Popular in San Diego',
          subtitle: 'Save your faves and ones you might try.',
          items: filterSaved(popularSanDiego),
        },
        ...(savedPlaces.length > 0
          ? [
              {
                key: 'saved',
                title: '🔖 Saved',
                subtitle: "Places you've saved here.",
                items: savedPlaces,
                boxRef: savedPlacesRef,
              },
            ]
          : []),
      ].filter((section) => section.items.length > 0);
    },
    [nearestYou, popularNeighbors, popularSanDiego, savedPlaces, savedIds],
  );

  const renderCard = (place: Place) => {
    const isSaved = savedIds.has(place.id.toString());
    return (
      <Box key={place.id} position="relative">
        <PlaceCard place={place} modal />
        <BookmarkButton
          isBookmarked={isSaved}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Fly-to-save: only on add. Capture the (+) button's center now,
            // before React rerenders, so the animation starts where the user tapped.
            if (!isSaved) {
              const target = e.currentTarget as HTMLElement | null;
              const rect = target?.getBoundingClientRect();
              if (rect) {
                triggerFlyToSave({
                  x: rect.left + rect.width / 2,
                  y: rect.top + rect.height / 2,
                });
              }
            }
            handleToggleBookmark(place);
          }}
          position="absolute"
          top="12px"
          right="12px"
          size="36px"
          iconSize="14px"
          zIndex={20}
        />
      </Box>
    );
  };

  const containerProps = embedded
    ? {position: 'relative' as const, w: '100%', h: '100%'}
    : {
        position: 'fixed' as const,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      };

  return (
    <Box {...containerProps} display="flex" flexDirection="column" bg="white">
      <Modal
        isOpen={showExitConfirmation}
        onClose={() => setShowExitConfirmation(false)}
        isCentered>
        <ModalOverlay />
        <ModalContent w="90%" maxW="90vw" borderRadius="12px" p={0}>
          <ModalBody p={6} textAlign="center">
            <VStack spacing={4}>
              <Text fontSize="40px">🔖</Text>
              <TextG24 fontSize="26px" fontWeight="600" color="#262626">
                Save at least 3 things
              </TextG24>
              <TextG16 color="#262626" lineHeight="1.5">
                You will always want{' '}
                <Text as="span" fontWeight="700">
                  at least 3 things
                </Text>{' '}
                saved in the future so Walkabout can pair you with interested neighbors.
              </TextG16>
              <CustomButton
                background="#C9136B"
                color="white"
                w="100%"
                h="44px"
                borderRadius="100px"
                boxShadow="0 -4px 0 0 rgba(0, 0, 0, 0.25) inset"
                onClick={() => setShowExitConfirmation(false)}
                mt={2}>
                <TextG14 fontWeight="600" color="white">
                  Keep Saving
                </TextG14>
              </CustomButton>
              <Text
                fontSize="14px"
                color="#757D84"
                cursor="pointer"
                onClick={() => {
                  if (embedded) {
                    onExitConfirmationSkip?.();
                  } else {
                    setShowExitConfirmation(false);
                    navigate('/home');
                  }
                }}
                textDecoration="underline">
                Skip For Now
              </Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {searchParams.has('id') && (
        <ArticleModal
          open={isOpen}
          close={() => {
            searchParams.delete('id');
            searchParams.delete('articleType');
            setSearchParams(searchParams, {replace: true});
            closeArticleModal();
          }}
          type={loadType}
        />
      )}

      <Box
        flex={1}
        overflowY="auto"
        css={{
          '&::-webkit-scrollbar': {display: 'none'},
          scrollbarWidth: 'none',
        }}>
        <Box position="relative" w="100%">
          {!embedded && (
            <IconButton
              aria-label="Close"
              icon={<Icon iconName="fi fi-rr-cross" style={{fontSize: '12px', color: '#262626'}} />}
              position="absolute"
              top="32px"
              right="18px"
              w="48px"
              h="48px"
              minW="48px"
              bg="white"
              borderRadius="full"
              border="1px solid #E9E9E9"
              boxShadow="md"
              zIndex={5}
              onClick={handleClose}
            />
          )}
        </Box>
        <Box px={4} pt={4} pb={2} pr="66px">
          <TextG24 as="span" fontSize="26px" fontWeight="700" lineHeight="34px" color="#C9136B">
            Save places to explore{' '}
          </TextG24>
          <TextG24 as="span" fontSize="26px" lineHeight="34px" color="#262626" fontWeight="700">
            so we can keep you in the know.
          </TextG24>
        </Box>
        <Box
          ref={bannerRef}
          position="sticky"
          top={0}
          zIndex={101}
          bg="white"
          borderBottom="1px solid #E9E9E9"
          px={4}
          py={3}>
          <HStack align="center" spacing={2}>
            <BookmarkAheadLocationPicker
              selected={selectedLocation}
              onChange={setSelectedLocation}
            />
            {bookmarkedCount > 0 && (
              <HStack
                ref={savedPillRef as any}
                as="button"
                onClick={() => {
                  savedPlacesRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  });
                }}
                bg="white"
                border="1px solid #C9136B"
                borderRadius="100px"
                px={4}
                h="36px"
                spacing={2}
                flexShrink={0}>
                <Icon
                  iconName="fi fi-sr-bookmark"
                  style={{fontSize: '14px', color: '#C9136B', lineHeight: 1}}
                />
                <TextG14 mt={0} lineHeight="1" fontWeight="700" color="#C9136B">
                  {`${bookmarkedCount} Saved`}
                </TextG14>
              </HStack>
            )}
          </HStack>
        </Box>
        {loading ? (
          <Center py={8}>
            <Spinner size="lg" color="#C9136B" />
          </Center>
        ) : (
          <VStack spacing={0} align="stretch" px={4} pb="120px">
            {sections.map((section, idx) => (
              <Box key={section.key} ref={section.boxRef} mt={idx === 0 ? 0 : 6}>
                <Box
                  position="sticky"
                  top={`${bannerHeight}px`}
                  bg="white"
                  zIndex={100}
                  py={3}
                  px={4}
                  my={4}
                  mx={-4}
                  borderBottom="1px solid"
                  borderColor="gray.200">
                  <TextG16 fontWeight="700" color="#262626">
                    {section.title}
                  </TextG16>
                  <TextG12 color={theme.colors.grey} mt={1}>
                    {section.subtitle}
                  </TextG12>
                </Box>
                <VStack spacing={4} align="stretch">
                  {section.items.map(renderCard)}
                </VStack>
              </Box>
            ))}
          </VStack>
        )}
      </Box>

      <Box position="fixed" top={0} left={0} right={0} bottom={0} pointerEvents="none" zIndex={250}>
        <AnimatePresence>
          {flyingItems.map((item) => {
            const target = savedPillRef.current?.getBoundingClientRect();
            if (!target) return null;
            const toX = target.left + target.width / 2;
            const toY = target.top + target.height / 2;
            return (
              <motion.div
                key={item.key}
                initial={{
                  position: 'absolute',
                  left: item.from.x - 18,
                  top: item.from.y - 18,
                  opacity: 1,
                  scale: 1,
                }}
                animate={{
                  left: toX - 12,
                  top: toY - 12,
                  opacity: 0.9,
                  scale: 0.6,
                }}
                exit={{opacity: 0}}
                transition={{duration: 0.6, ease: [0.22, 1, 0.36, 1]}}
                onAnimationComplete={() => removeFlyingItem(item.key)}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: '#C9136B',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0px 4px 12px rgba(201, 19, 107, 0.35)',
                }}>
                <Icon
                  iconName="fi fi-sr-bookmark"
                  style={{fontSize: '14px', color: 'white', lineHeight: 1}}
                />
              </motion.div>
            );
          })}
        </AnimatePresence>
      </Box>

      {/* In onboarding (embedded), the onboarding shell's Continue button drives
          advancement and the 3-saves gate. Rendering the sticky "I'm Done!" bar and
          success modal here would fork the flow and let the modal's CTAs navigate the
          user out of onboarding entirely. */}
      <AnimatePresence>
        {!embedded && bookmarkedCount > 0 && !showSuccessModal && (
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.3}}
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 200,
            }}>
            <Box
              bg="white"
              borderTop="1px solid"
              borderColor="gray.200"
              px={4}
              py={4}
              boxShadow="0 -2px 10px rgba(0, 0, 0, 0.1)">
              <BlueButton
                w="100%"
                h="50px"
                background="#C9136B"
                borderRadius="100px"
                boxShadow="0 4px 0 0 rgba(0, 0, 0, 0.25)"
                onClick={() => {
                  if (needsExitConfirmation) {
                    setShowExitConfirmation(true);
                  } else {
                    setShowSuccessModal(true);
                  }
                }}>
                <TextG16 color="white" fontWeight="600">
                  I'm Done!
                </TextG16>
              </BlueButton>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      <Modal
        isOpen={!embedded && showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        isCentered>
        <ModalOverlay bg="rgba(0, 0, 0, 0.6)" />
        <ModalContent w="90%" maxW="90vw" borderRadius="16px" p={0} overflow="hidden">
          <ModalBody p={6}>
            <VStack spacing={4} align="stretch">
              <Box
                w="44px"
                h="44px"
                borderRadius="12px"
                bg="#FAE7F0"
                display="flex"
                alignItems="center"
                justifyContent="center">
                <Icon iconName="fi fi-sr-sparkles" style={{fontSize: '22px', color: '#C9136B'}} />
              </Box>
              <TextG24 fontSize="22px" lineHeight="28px" fontWeight="700" color="#262626">
                Nice! Your neighborhood is all set
              </TextG24>
              <TextG14 color="#4D4D4D" lineHeight="22px">
                Now you'll meet neighbors who love what you love, plus get perks at your{' '}
                {bookmarkedCount} saved {bookmarkedCount === 1 ? 'spot' : 'spots'}.
              </TextG14>
              <CustomButton
                background="#C9136B"
                color="white"
                w="100%"
                h="48px"
                borderRadius="100px"
                boxShadow="0 -4px 0 0 rgba(0, 0, 0, 0.25) inset"
                onClick={() => {
                  setShowSuccessModal(false);
                  if (onClose) onClose();
                  else navigate('/home');
                }}>
                <TextG14 fontWeight="600" color="white">
                  Go to Home
                </TextG14>
              </CustomButton>
              <Box
                as="button"
                h="44px"
                borderRadius="100px"
                border="1px solid #E9E9E9"
                bg="white"
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/my-stuff?tab=Saves');
                }}>
                <TextG14 fontWeight="500" color="#262626">
                  See all your saves in My Stuff
                </TextG14>
              </Box>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default BookmarkPlaces;
